import { Calculator, Sun, Gauge, DollarSign, ClipboardCheck, MapPin, Building2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Fragment } from 'react';
import { ToolAdCard } from './ads/ToolAdCard';

const tools = [
  {
    icon: Calculator,
    title: 'Bill Calculator',
    description: 'Calculate your electricity bill based on usage and DisCo tariffs across all bands.',
    color: 'bg-[#C1272D]',
    textColor: 'text-[#C1272D]',
    bgLight: 'bg-red-50',
    path: '/tools/bill-calculator',
    featured: true,
  },
  {
    icon: Sun,
    title: 'Solar Sizing Tool',
    description: 'Find the perfect solar system size for your home or business with ROI calculations.',
    color: 'bg-[#FFD700]',
    textColor: 'text-[#8a5a00]',
    bgLight: 'bg-yellow-50',
    path: '/tools/solar-sizing',
  },
  {
    icon: DollarSign,
    title: 'Outage Cost Calculator',
    description: 'Calculate the true cost of power outages including generator fuel and lost productivity.',
    color: 'bg-[#008751]',
    textColor: 'text-[#006b40]',
    bgLight: 'bg-green-50',
    path: '/tools/outage-calculator',
  },
  {
    icon: Gauge,
    title: 'Tariff Band Checker',
    description: 'Find out which electricity tariff band you fall under based on your location and supply hours.',
    color: 'bg-[#008751]',
    textColor: 'text-[#006b40]',
    bgLight: 'bg-green-50',
    path: '/tools/tariff-checker',
  },
  {
    icon: ClipboardCheck,
    title: 'Energy Audit',
    description: 'Take a quick quiz to get personalized energy-saving recommendations for your home.',
    color: 'bg-[#008751]',
    textColor: 'text-[#006b40]',
    bgLight: 'bg-green-50',
    path: '/tools/energy-audit',
  },
  {
    icon: MapPin,
    title: 'Refinery Tracker',
    description: 'Interactive map of all Nigerian refineries with capacity, status, and production data.',
    color: 'bg-[#000000]',
    textColor: 'text-black',
    bgLight: 'bg-gray-50',
    path: '/tools/refinery-tracker',
  },
  {
    icon: Building2,
    title: 'DisCo Directory',
    description: 'Complete directory of electricity distribution companies with contact info and service areas.',
    color: 'bg-[#008751]',
    textColor: 'text-[#006b40]',
    bgLight: 'bg-green-50',
    path: '/tools/disco-directory',
  },
];

export function ToolsGrid() {
  return (
    <section id="tools" className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Interactive Energy Tools
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Calculate costs, plan solar installations, and learn energy management through our comprehensive toolkit
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tools.slice(0, 2).map((tool, index) => {
            const Icon = tool.icon;
            const colSpan = tool.featured ? 'md:col-span-2' : '';

            const ToolCard = (
              <Link
                key={index}
                to={tool.path}
                className={`${colSpan} ${tool.bgLight} rounded-xl p-6 text-left hover:shadow-lg transition-all duration-300 hover:scale-105 border-2 border-transparent hover:border-[#008751]`}
              >
                <div className={`${tool.color} w-12 h-12 rounded-lg flex items-center justify-center mb-4`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className={`text-xl font-bold mb-2 ${tool.textColor}`}>
                  {tool.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {tool.description}
                </p>
                {tool.featured && (
                  <div className="mt-4 inline-block bg-[#C1272D] text-white text-xs px-3 py-1 rounded-full">
                    Featured Tool
                  </div>
                )}
              </Link>
            );

            if (index === 1) {
              return (
                <Fragment key={index}>
                  {ToolCard}
                  <ToolAdCard />
                </Fragment>
              );
            }

            return ToolCard;
          })}
        </div>

        {/* View All Tools Button */}
        <div className="text-center mt-8">
          <Link
            to="/tools"
            className="inline-flex items-center gap-2 bg-[#008751] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#006d41] transition-colors"
          >
            View All Tools
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
